import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ReservePage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [weekly, setWeekly] = useState([]);
  const [events, setEvents] = useState([]);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    party_size: 2, date: "", time: "19:30", special_request: "",
    weekly_menu_id: params.get("menu") || "", event_id: params.get("event") || "",
  });

  useEffect(() => {
    api.get("/weekly-menus").then((r) => setWeekly(r.data.filter((m) => m.available))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.filter((e) => e.status === "publie"))).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      setForm((f) => ({ ...f, first_name: user.first_name || "", last_name: user.last_name || "", email: user.email || "", phone: user.phone || f.phone }));
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, party_size: Number(form.party_size), weekly_menu_id: form.weekly_menu_id || null, event_id: form.event_id || null };
      const { data } = await api.post("/reservations", payload);
      setDone(data);
      toast.success("Réservation envoyée ! Nous vous confirmerons rapidement.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="reservation-confirmation">
        <CheckCircle2 size={56} className="text-[#009246] mx-auto mb-6" />
        <h1 className="font-serif-elegant text-4xl font-bold mb-4">Merci, {done.first_name} !</h1>
        <p className="text-gray-300 leading-relaxed">
          Votre demande de réservation pour <span className="text-[#D4AF37]">{done.party_size} personne(s)</span> le{" "}
          <span className="text-[#D4AF37]">{new Date(done.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {done.time}</span>{" "}
          a bien été envoyée. Vous recevrez une confirmation de notre équipe.
        </p>
        <button onClick={() => setDone(null)} data-testid="reservation-new-btn" className="btn-outline-gold mt-8">Nouvelle réservation</button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="reserve-page">
      <p className="gold-label mb-3">Réservation en ligne</p>
      <h1 className="font-serif-elegant text-4xl sm:text-5xl font-bold mb-10">Réserver une table</h1>
      <form onSubmit={submit} className="ic-card !transform-none p-8 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="gold-label block mb-2">Prénom *</label>
            <input data-testid="reserve-firstname" required className="ic-input" value={form.first_name} onChange={set("first_name")} placeholder="Marco" />
          </div>
          <div>
            <label className="gold-label block mb-2">Nom *</label>
            <input data-testid="reserve-lastname" required className="ic-input" value={form.last_name} onChange={set("last_name")} placeholder="Rossi" />
          </div>
          <div>
            <label className="gold-label block mb-2">Téléphone *</label>
            <input data-testid="reserve-phone" required type="tel" className="ic-input" value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" />
          </div>
          <div>
            <label className="gold-label block mb-2">Email *</label>
            <input data-testid="reserve-email" required type="email" className="ic-input" value={form.email} onChange={set("email")} placeholder="marco@email.fr" />
          </div>
          <div>
            <label className="gold-label block mb-2">Nombre de personnes *</label>
            <input data-testid="reserve-partysize" required type="number" min="1" max="150" className="ic-input" value={form.party_size} onChange={set("party_size")} />
          </div>
          <div>
            <label className="gold-label block mb-2">Date *</label>
            <input data-testid="reserve-date" required type="date" className="ic-input" value={form.date} onChange={set("date")} min={new Date().toISOString().split("T")[0]} />
          </div>
          <div>
            <label className="gold-label block mb-2">Heure *</label>
            <input data-testid="reserve-time" required type="time" className="ic-input" value={form.time} onChange={set("time")} />
          </div>
        </div>
        <div>
          <label className="gold-label block mb-2">Menu de la semaine (optionnel)</label>
          <select data-testid="reserve-weekly-menu" className="ic-input" value={form.weekly_menu_id} onChange={set("weekly_menu_id")}>
            <option value="">Aucun menu sélectionné</option>
            {weekly.map((m) => (
            <option key={m.id} value={m.id}>{`${m.title} — ${m.price.toFixed(2).replace(".", ",")} €`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="gold-label block mb-2">Soirée / Événement (optionnel)</label>
          <select data-testid="reserve-event" className="ic-input" value={form.event_id} onChange={set("event_id")}>
            <option value="">Aucune soirée sélectionnée</option>
            {events.map((e) => (
            <option key={e.id} value={e.id}>{`${e.title} — ${new Date(e.date + "T00:00:00").toLocaleDateString("fr-FR")}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="gold-label block mb-2">Demande spéciale</label>
          <textarea data-testid="reserve-special-request" rows="3" className="ic-input" value={form.special_request} onChange={set("special_request")} placeholder="Anniversaire, allergie, table en terrasse…" />
        </div>
        <button type="submit" disabled={submitting} data-testid="reserve-submit-btn" className="btn-gold w-full !py-3.5 disabled:opacity-50">
          {submitting ? "Envoi en cours…" : "Confirmer ma réservation"}
        </button>
      </form>
    </div>
  );
}
